/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import classNameBind from 'classnames/bind';
import QRCode from 'react-qr-code';
import { FormikProps } from 'formik';
import { CopyIcon, withInject } from '@piwikpro/platform';
import {
  Stack,
  Information,
  Text,
  Spinner,
  Card,
  Trans,
  withTranslation,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { FormValidator } from '@piwikpro/form-crate';
import { httpErrors } from '@piwikpro/http-crate';
import { Title as TitleComponent } from '@piwikpro/title-crate';
import { TwoFactorAuthSetupData, UserAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';
import { AutoSavedVerificationCodeInputControl } from '../VerificationCodeInput';
import styles from './TwoFactorConfigure.module.css';
import { FailMessage } from '../../../../components/FailMessage';
import TwoFactorConfirmCurrent from './TwoFactorConfirmCurrent';

export const getSecretFromTotpUri = (totpUri: string): string => {
  const match = totpUri.match(/secret=(.*?)&/i);
  if (match) {
    return match[1];
  }

  return '';
};

const TWO_FACTOR_INPUT_NUMBER_OF_FIELDS = 6;

const bindClasses = classNameBind.bind(styles);

export interface ITwoFactorConfigureProps {
  authService: AuthSettings
  validation: FormValidator
  failed: UserAuthSettingsState['failed']
  data: TwoFactorAuthSetupData | null
  failReason: any //TODO: try to type better
  isLoading: UserAuthSettingsState['isLoading']
  Title: typeof TitleComponent
  twoFactorAuthEnabled: boolean
  isResetFlow: boolean
  t: (key: string) => string
}

interface IFormValues {
  code: string
}

export const TwoFactorConfigure = ({
  authService,
  validation,
  failed,
  failReason,
  isLoading,
  data,
  isResetFlow,
  twoFactorAuthEnabled,
  Title,
  t,
}: ITwoFactorConfigureProps) => {
  const secret = getSecretFromTotpUri(data?.totp_uri || '');
  const [currentCodeViewVisible, setCurrentCodeViewVisible] = useState(twoFactorAuthEnabled);
  useEffect(() => {
    const fetchConfigData = async () => {
      if (!currentCodeViewVisible) {
        try {
          await authService.setup2faForCurrentUser();
        // eslint-disable-next-line no-empty
        } catch {}
      }
    };

    fetchConfigData();
  }, []);

  const handleSubmit = async (
    values: IFormValues,
    api: FormikProps<IFormValues>,
  ) => {
    try {
      await authService.enter2faCodeForCurrentUser(values.code, twoFactorAuthEnabled);

      api.setSubmitting(false);
    } catch {
      api.resetForm();
      api.setSubmitting(false);
    }
  };

  if (currentCodeViewVisible) {
    return <TwoFactorConfirmCurrent setCurrentCodeViewVisible={setCurrentCodeViewVisible} />;
  }

  if (isLoading) {
    return (
      <Card.Section>
        <div className={bindClasses('card-extension')}>
          <Spinner className="large" />
        </div>
      </Card.Section>
    );
  }

  if (failReason && failReason !== httpErrors.InvalidRequestError) {
    return (
      <Card.Section>
        <div className={bindClasses('card-extension')}>
          <FailMessage />
        </div>
      </Card.Section>
    );
  }

  return (
    <Title part={t(isResetFlow ? 'security.2fa-reset.title' : 'security.confirmCurrentModal.title')} subset>
      <Card.Section>
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item>
            <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
              {t(isResetFlow ? 'security.2fa.configure.process.resetFlow.header' : 'security.2fa.configure.process.turnOnFlow.header')}
            </Text>
          </Stack.Item>
          <Stack.Item fill>
            <div style={{ textAlign: 'left' }}>
              <Text as="p">
                <Trans
                  t={t}
                  i18nKey={isResetFlow
                    ? 'security.2fa.configure.process.resetFlow.step1.description'
                    : 'security.2fa.configure.process.turnOnFlow.step1.description'}
                  components={[
                    <b>text</b>,
                  ]}
                />
              </Text>
            </div>
          </Stack.Item>
          <Stack.Item fill previousSpacing="narrow">
            <div style={{ textAlign: 'left' }}>
              <Text as="p">
                <Trans
                  t={t}
                  i18nKey="security.2fa.configure.process.step2.description"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </Text>
            </div>
          </Stack.Item>
          <Stack.Item>
            {/* white styling added to fix QR code reading issues in dark mode */}
            <div style={{ backgroundColor: 'white', padding: '4px 4px 0px 4px' }}>
              <QRCode
                size={256}
                style={{ height: '189px', width: '189px' }}
                value={data?.totp_uri || ''}
                viewBox="0 0 256 256"
              />
            </div>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              <Trans
                t={t}
                i18nKey="security.2fa.configure.process.step2.backupCode.helpText"
                components={[
                  <b>text</b>,
                ]}
              />
            </Text>
          </Stack.Item>
          <Stack.Item previousSpacing="narrow">
            <div className={bindClasses('copy-content-wrapper')}>
              <Text as="p" size={12} color="InkLighter" dataId="Caption">{secret}</Text>
              <CopyIcon value={secret} />
            </div>
          </Stack.Item>
          <Stack.Item fill>
            <div style={{ textAlign: 'left' }}>
              <Text as="p">
                <Trans
                  t={t}
                  i18nKey="security.2fa.configure.process.step3.description"
                  components={[
                    <b>text</b>,
                  ]}
                />
              </Text>
            </div>
          </Stack.Item>
          {failed && (
            <Stack.Item fill>
              <Information type="error">{t('security.invalidAuthCode.info')}</Information>
            </Stack.Item>
          )}
          <Stack.Item>
            <AutoSavedVerificationCodeInputControl
              // @ts-ignore
              id="two-factor-configuration-form" // TODO: can be removed as it doesn't apply by default (Form problem?)
              name="code"
              initialValue=""
              validationSchema={validation.createValidationSchema(
                () => ({
                  code: validation
                    .getYup()
                    .string()
                    .min(TWO_FACTOR_INPUT_NUMBER_OF_FIELDS)
                    .required(),
                }),
              )}
              onSubmit={handleSubmit}
            />
          </Stack.Item>
        </Stack>
      </Card.Section>
    </Title>
  );
};

export default withInject({
  validation: 'FormCrate.validation',
  authService: 'AuthCrate.authSettings',
  Title: 'TitleCrate.components.Title',
})(withTranslation(['profile'])(TwoFactorConfigure));
